/**
 * paid-s (SENSE - vision + hearing) results view-model + builder (sense-2.0).
 *
 * Rebuilt from the old 6-subtest / 0-100 composite model. There is NO score, NO
 * band, NO percentile, NO norm here, by design (handoff 02/03). The card shows
 * two things per metric: that it was saved, and - from the second scan on - how it
 * moved against the woman's own previous measured scan. A non-ok metric is "not
 * measured", never a number. First scan is the MAIN screen, not an edge case.
 */
import {
  SenseRecord,
  SenseMetric,
  isSense2,
  metricValue,
  metricStatus,
  computeChange,
  senseEngineOf,
} from './sense-model';

export type SenseRowState =
  | 'saved'
  | 'notMeasured'
  | 'notThisTime'
  | 'stable'
  | 'changed';

export interface SenseRowVM {
  metric: SenseMetric;
  labelKey: string;
  state: SenseRowState;
  /** dir copy key when state==='changed' (up = the metric's "better" direction). */
  dirKey: string | null;
  /** formatted date of the previous SUCCESSFULLY measured scan, when changed. */
  sinceDate: string | null;
  detailTitleKey: string;
  whatKey: string;
  /** technical raw value for the expandable detail, e.g. "logCS 1.76". null if not ok. */
  rawValue: string | null;
}

export interface SenseChartVM {
  has: boolean; // >=2 ok points on this metric
  /** each polyline segment; a gap (inconclusive scan) breaks the line, never bridged. */
  segments: { x: number; y: number }[][];
  dots: { x: number; y: number }[];
}

export interface SenseSeriesVM {
  metric: SenseMetric;
  labelKey: string;
  chart: SenseChartVM;
}

export interface SensePanelVM {
  available: boolean;
  firstScan: boolean;
  rows: SenseRowVM[];
  vision: SenseSeriesVM;
  hearing: SenseSeriesVM;
  hasGraph: boolean;
  /** the woman has old sense-1.x history, so show the "tracking starts here" line. */
  engineChanged: boolean;
}

const METRICS: SenseMetric[] = ['vision', 'hearing'];
const ROW_LABEL: Record<SenseMetric, string> = {
  vision: 'results.se.rowVision',
  hearing: 'results.se.rowHearing',
};
const DETAIL_KEY: Record<SenseMetric, string> = {
  vision: 'results.se.subtests.contrast',
  hearing: 'results.se.subtests.sound',
};

const CHART_W = 280;
const CHART_H = 54;
const CHART_PAD = 9;

function rawLabel(metric: SenseMetric, v: number): string {
  return metric === 'vision'
    ? `logCS ${v.toFixed(2)}`
    : `SNR ${v.toFixed(1)} dB`;
}

/** Build one metric's trend chart across ALL sense-2.0 scans. Inconclusive scans
 *  leave a hole: they are not plotted and the line is not bridged over them. */
function buildChart(history: SenseRecord[], metric: SenseMetric): SenseChartVM {
  const n = history.length;
  const xs = history.map((_, i) =>
    n > 1 ? CHART_PAD + (i / (n - 1)) * (CHART_W - 2 * CHART_PAD) : CHART_W / 2
  );
  // Orient each metric so "better" is UP. Vision logCS is higher=better (as-is);
  // hearing SNR is lower=better, so plot the negated value. Without this a worse
  // hearing result (higher SNR) draws an up-sloping line that reads as an
  // improvement and contradicts the change text ("fainter noise" = declined).
  const orient = metric === 'hearing' ? -1 : 1;
  const vals = history.map((r) => {
    const v = metricValue(r, metric);
    return v == null ? null : v * orient;
  });
  const ok = vals.filter((v): v is number => v != null);
  if (ok.length < 2) return { has: false, segments: [], dots: [] };
  let min = Math.min(...ok);
  let max = Math.max(...ok);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const y = (v: number) =>
    CHART_H - CHART_PAD - ((v - min) / (max - min)) * (CHART_H - 2 * CHART_PAD);
  const dots: { x: number; y: number }[] = [];
  const segments: { x: number; y: number }[][] = [];
  let seg: { x: number; y: number }[] = [];
  vals.forEach((v, i) => {
    if (v == null) {
      if (seg.length) {
        segments.push(seg);
        seg = [];
      }
      return;
    }
    const pt = { x: xs[i], y: y(v) };
    dots.push(pt);
    seg.push(pt);
  });
  if (seg.length) segments.push(seg);
  return { has: true, segments, dots };
}

/**
 * @param current  the viewed scan's sense record (sense-2.0) or null
 * @param history  ALL sense-2.0 records up to AND INCLUDING the viewed scan, asc
 * @param opts.engineChanged  the user also has sense-1.x history
 * @param opts.fmtDate  ISO -> short display date
 */
export function buildSenseVM(
  current: SenseRecord | null,
  history: SenseRecord[],
  opts: { engineChanged: boolean; fmtDate: (iso: string) => string }
): SensePanelVM {
  const available = isSense2(current);
  // Only records from the SAME engine version are comparable: the 2026-09 package
  // changed the staircase, so a 2.0 point and a 2.1 point are a different baseline
  // (02_VALIDITY sec ד / spec §8). Restrict the trend + change comparison to the
  // current scan's engine; earlier records on another engine are a prior baseline,
  // which surfaces as the "tracking starts fresh here" (newBaseline) line.
  const currentEngine = senseEngineOf(current);
  const sameEngine = currentEngine
    ? history.filter((r) => senseEngineOf(r) === currentEngine)
    : history;
  const priorOtherEngine = history.some(
    (r) => senseEngineOf(r) !== currentEngine
  );
  // previous same-engine records (everything before the viewed scan)
  const prior = sameEngine.slice(0, Math.max(0, sameEngine.length - 1));
  const firstScan = prior.length === 0;

  const rows: SenseRowVM[] = METRICS.map((metric) => {
    const cur = metricValue(current, metric);
    const detailTitleKey = `${DETAIL_KEY[metric]}.title`;
    const whatKey = `${DETAIL_KEY[metric]}.what`;
    const labelKey = ROW_LABEL[metric];
    const rawValue = cur != null ? rawLabel(metric, cur) : null;

    // not measured this time (or, on a first scan, simply not measured)
    if (cur == null) {
      return {
        metric,
        labelKey,
        state: firstScan ? 'notMeasured' : 'notThisTime',
        dirKey: null,
        sinceDate: null,
        detailTitleKey,
        whatKey,
        rawValue,
      };
    }

    // most recent previous scan where THIS metric was ok
    let prevRec: SenseRecord | null = null;
    for (let i = prior.length - 1; i >= 0; i--) {
      if (metricValue(prior[i], metric) != null) {
        prevRec = prior[i];
        break;
      }
    }
    if (!prevRec) {
      return {
        metric,
        labelKey,
        state: 'saved',
        dirKey: null,
        sinceDate: null,
        detailTitleKey,
        whatKey,
        rawValue,
      };
    }

    const change = computeChange(metric, current, prevRec);
    if (change.state === 'stable') {
      return {
        metric,
        labelKey,
        state: 'stable',
        dirKey: null,
        sinceDate: null,
        detailTitleKey,
        whatKey,
        rawValue,
      };
    }
    const dir = change.state === 'improved' ? 'up' : 'down';
    return {
      metric,
      labelKey,
      state: 'changed',
      dirKey: `results.se.dir.${metric}.${dir}`,
      sinceDate: opts.fmtDate(prevRec.taken_at || ''),
      detailTitleKey,
      whatKey,
      rawValue,
    };
  });

  const vision: SenseSeriesVM = {
    metric: 'vision',
    labelKey: ROW_LABEL.vision,
    chart: buildChart(sameEngine, 'vision'),
  };
  const hearing: SenseSeriesVM = {
    metric: 'hearing',
    labelKey: ROW_LABEL.hearing,
    chart: buildChart(sameEngine, 'hearing'),
  };

  return {
    available,
    firstScan,
    rows,
    vision,
    hearing,
    hasGraph: vision.chart.has || hearing.chart.has,
    // "tracking starts here" when there is prior history on a DIFFERENT engine —
    // either the old 1.x model (opts.engineChanged) or an earlier 2.x baseline.
    engineChanged: opts.engineChanged || priorOtherEngine,
  };
}

// re-export for consumers that only import from the vm barrel
export { metricStatus };
